import { GridApi } from '@amzn/gft-ag-grid-enterprise';
import { AurigaGrid } from '@components/Common/AurigaGrid/AurigaGrid';
import RightClickContextMenu from '@components/Common/RightClickContextMenu/RightClickContextMenu';
import { globalGridDefaults } from '@constants/agGrid';
import { useDecklinesContext } from '@context/DecklinesContext';
import { useHighlightingContext } from '@context/HighlightingContext';
import { usePageContext } from '@context/PageContext';
import useGridReady from '@deckManagement/hooks/useGridReady';
import { find, isEmpty, isNil } from 'lodash';
import { useEffect, useRef } from 'react';
import { SectionGridProps } from 'types/SectionGrid.types';

const SectionGrid = ({
  sectionLookupKey,
  sectionRowData,
  sectionGridOptions,
  contextMenuOptions,
  contextMenuHandler,
  gridManager,
  isMultiSectionPage,
}: SectionGridProps) => {
  const { clearSubtotalHighlighting } = useHighlightingContext();
  const { pageRowData } = usePageContext();
  const { selectedDeckline } = useDecklinesContext();
  // Reference to the AGGrid instance
  const gridApiRef = useRef<GridApi | null>(null);
  // Grid hook with utility functions
  const { onGridReady } = useGridReady(gridApiRef, {
    gridManager,
    sectionLookupKey,
  });
  // Previous selected Deckline id reference
  const prevSelectedDecklineIdRef = useRef<string | undefined>(undefined);

  /**
   * Ensures that AGGrid is focused on the correct row when `selectedDeckline` changes.
   */
  useEffect(() => {
    // Only clear highlighting when moving to a different row
    const currentDecklineId = selectedDeckline?.deckline_id;
    if (prevSelectedDecklineIdRef.current && prevSelectedDecklineIdRef.current !== currentDecklineId) {
      // Clear highlights for subtotals when no longer relevant, based on previous deckline selected
      const prevDeckline = find(
        pageRowData,
        (row) => row.rawDecklineData.deckline_id === prevSelectedDecklineIdRef.current,
      );

      // Only clear highlighting if the previous deckline had a subtotal formula
      if (
        prevDeckline &&
        !isNil(prevDeckline?.rawDecklineData.subtotal_formula) &&
        !isEmpty(prevDeckline?.rawDecklineData.subtotal_formula)
      ) {
        // Important*: Clear highlighting depends on the full PageRowData, not a section subset
        clearSubtotalHighlighting(pageRowData);
      }
    }
    prevSelectedDecklineIdRef.current = currentDecklineId;

    if (selectedDeckline) {
      const targetRowIndex = sectionRowData.findIndex(
        (row) => row.rawDecklineData.deckline_id === selectedDeckline.deckline_id,
      );
      // Only set focus if the rowIndex doesn't match and exists in this Section's grid.
      if (targetRowIndex >= 0 && gridApiRef.current?.getFocusedCell()?.rowIndex !== targetRowIndex) {
        gridApiRef.current?.setFocusedCell(targetRowIndex, 'kpi_name');
      }
    }
  }, [sectionRowData, selectedDeckline, clearSubtotalHighlighting, pageRowData]);

  // To track the threshold for autoHeight
  const isAutoHeightEligible = sectionRowData.length < 16;

  /**
   *  Note: The below use of 'domLayout' auto reduces the height of grids in a Multi-Grid view.
   *  This is an expensive compromise, so used sparingly, as it forces the grid to load all rows in memory to achieve the resize.
   *  Customize as needed in the future, but be aware of the performance implications as this should not be applied to single-section grids with many rows.
   *  See: https://www.ag-grid.com/react-data-grid/grid-size/
   */
  return (
    <RightClickContextMenu onAction={contextMenuHandler} options={contextMenuOptions}>
      <AurigaGrid
        {...globalGridDefaults}
        {...sectionGridOptions}
        className={`section-grid-container ${isMultiSectionPage && isAutoHeightEligible ? 'multi-section-auto' : ''}`}
        domLayout={isAutoHeightEligible ? 'autoHeight' : 'normal'}
        onGridReady={onGridReady}
        // Auto-size section row columns in the section view, once data has loaded
        onFirstDataRendered={(params) => {
          params.api.sizeColumnsToFit();
          params.api.autoSizeAllColumns(false);

          // Scroll to the selected deckline if one exists (e.g., after grid re-initialization)
          if (selectedDeckline) {
            const targetRowIndex = sectionRowData.findIndex(
              (row) => row.rawDecklineData.deckline_id === selectedDeckline.deckline_id,
            );
            if (targetRowIndex >= 0) {
              params.api.ensureIndexVisible(targetRowIndex, 'middle');
            }
          }
        }}
      />
    </RightClickContextMenu>
  );
};

export default SectionGrid;
